import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface SEOProps {
    title?: string;
    description?: string;
    keywords?: string;
    image?: string;
    url?: string;
}

const SEO: React.FC<SEOProps> = ({
    title = 'Fly Catering - Premium Airline Catering Solutions',
    description = 'Leading provider of premium airline catering services worldwide. Delivering exceptional culinary experiences at 30,000 feet.',
    keywords = 'airline catering, inflight meals, aviation food service, premium catering, airline food, inflight dining',
    image = 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200',
    url = 'https://flycatering.com'
}) => {
    const { i18n } = useTranslation();
    const location = useLocation();

    const siteUrl = process.env.REACT_APP_SITE_URL || 'https://ala-catering.kz';
    const fullUrl = url || `${siteUrl}${location.pathname}`;
    const language = i18n.language || 'en';
    const ogLocale = language === 'ru' ? 'ru_RU' : language === 'kk' ? 'kk_KZ' : 'en_US';

    const organizationSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Almaty Catering Services LLP',
        url: siteUrl,
        email: ['acs-dispatch@ala-catering.kz', 'sales@ala-catering.kz'],
        telephone: ['+7 (727) 3838880', '+77010260642'],
        logo: `${siteUrl}/logo.png`
    };

    const websiteSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Almaty Catering Services',
        url: siteUrl,
        inLanguage: [
            'en',
            'ru',
            'kk'
        ]
    };

    return (
        <Helmet>
            <html lang={language} />
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:site_name" content="Almaty Catering Services" />
            <meta property="og:locale" content={ogLocale} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />

            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={fullUrl} />
            <meta property="twitter:title" content={title} />
            <meta property="twitter:description" content={description} />
            <meta property="twitter:image" content={image} />

            {/* Additional SEO tags */}
            <meta name="robots" content="index, follow" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
            <link rel="canonical" href={fullUrl} />
            <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
            <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        </Helmet>
    );
};

export default SEO;
